import * as React from "react"
import { ChakraProvider } from "@chakra-ui/react"
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom"
import { QueryClient, QueryClientProvider } from "react-query"
import { fetcher } from "./lib/fetcher"
import { theme } from "./lib/theme"
import { Page } from "./components/Page"
import { AuthPage } from "./components/AuthPage"
import { MeProvider, MeContext } from "./components/providers/MeProvider"
import { OrgProvider, useOrgs } from "./components/providers/OrgProvider"
import { FeatFlagsProvider } from "./components/providers/FeatFlagProvider"
import { MapBBoxProvider } from "./components/providers/MapBBoxProvider"
import MapSettingsProvider from "./components/providers/MapSettingsProvider"
import { Dashboard } from "./pages/Dashboard"
import { Bikes } from "./pages/Bikes"
import { Login } from "./pages/Login"
import { Alerts } from "./pages/Alerts"
import { Zones } from "./pages/Zones"
import { Trackers } from "./pages/Trackers"
import { Tracker } from "./pages/Tracker"
import { Users } from "./pages/Users"
import { OrgSettings } from "./pages/OrgSettings"
import { ForgotPassword } from "./pages/ForgotPassword"
import { ResetPassword } from "./pages/ResetPassword"
import { Bike } from "./pages/Bike"
import { Trips } from "./pages/Trips"
import { Invitation } from "./pages/Invitation"
import { ProductionWarning } from "./components/ProductionWarning"
import { UpdateVersionWarning } from "./components/UpdateVersionWarning"
import GMapsApiLoadChecker from "./gmaps/GMapsApiLoadChecker"
import { ORGS_HAS_DASHBOARD } from "./lib/static/organisation"
import "mapbox-gl/dist/mapbox-gl.css"
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css"
import "./assets/stylesheets/index.css"
import "./lib/firebase"

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: ({ queryKey }: { queryKey: unknown }) => fetcher(queryKey as string),
      refetchOnWindowFocus: false,
    },
  },
})

export default function App() {
  return (
    <BrowserRouter>
      <ChakraProvider theme={theme}>
        <QueryClientProvider client={queryClient}>
          <MeProvider>
            <ProductionWarning />
            <UpdateVersionWarning />
            <Pages />
          </MeProvider>
        </QueryClientProvider>
      </ChakraProvider>
    </BrowserRouter>
  )
}

function Pages() {
  const user = React.useContext(MeContext)

  return user ? (
    <OrgProvider>
      <FeatFlagsProvider>
        <MapBBoxProvider>
          <MapSettingsProvider>
            <GMapsApiLoadChecker />
            <AuthPages />
          </MapSettingsProvider>
        </MapBBoxProvider>
      </FeatFlagsProvider>
    </OrgProvider>
  ) : (
    <Routes>
      <Route path="/" element={<AuthPage />}>
        <Route path="/" element={<Login />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset/:token/:uuid" element={<ResetPassword />} />
        <Route path="/invitation/:id" element={<Invitation />} />
      </Route>
      <Route path="*" element={<Redirect to="/" />} />
    </Routes>
  )
}

function AuthPages() {
  const { currentOrgs } = useOrgs()
  return (
    <Routes>
      <Route path="/" element={<Page />}>
        <Route path="/" element={<Redirect to="/assets" />} />

        <Route
          path="/dashboard"
          element={ORGS_HAS_DASHBOARD(currentOrgs) ? <Dashboard /> : <Redirect to="/assets" />}
        />
        <Route path="/assets" element={<Bikes />} />
        <Route path="/assets/:id" element={<Bike />} />
        <Route path="/assets/:id/trips" element={<Trips />} />
        <Route path="/assets/:id/alerts" element={<Alerts />} />
        <Route path="/alerts" element={<Alerts />} />
        <Route path="/zones" element={<Zones />} />
        <Route path="/trackers" element={<Trackers />} />
        <Route path="/trackers/:id" element={<Tracker />} />
        <Route path="/users" element={<Users />} />
        <Route path="/organisation-settings" element={<OrgSettings />} />
      </Route>
      <Route path="*" element={<Redirect to="/" />} />
    </Routes>
  )
}

function Redirect(props: { to: string }) {
  // Update to use react-router's own component when they release
  const navigate = useNavigate()
  React.useEffect(() => {
    navigate(props.to)
  }, [props.to, navigate])
  return null
}
